import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { zql } from "../generatedDrizzleSchema";
import { BUILTIN_ROLE_IDS, type BuiltinRoleId } from "../rbac/roles";
import {
  grantEntry,
  insertGrantsDeduped,
  validateGrantsInput,
} from "../rbac/validation";
import { assertRoleDoesNotGrant } from "../rbac/validation/grantContent";
import { slugifyName, slugValidationError } from "../slug";
import { USER_ASSIGNABLE_SPACE_MODES } from "../spaceMode";
import {
  assertNameAvailable,
  assertSlugAvailable,
  spaceNameField,
  spaceSlugField,
} from "../spaceName";
import { mutator } from "./mutator";

export const createSpace = mutator({
  schema: z.object({
    name: spaceNameField,
    id: z.string(),
    slug: spaceSlugField.optional(),
    mode: z.enum(USER_ASSIGNABLE_SPACE_MODES),
    grants: z.array(grantEntry).optional(),
    defaultJoinBuiltinRoleId: z.enum(BUILTIN_ROLE_IDS).nullable().optional(),
    defaultJoinCustomRoleId: z.string().nullable().optional(),
  }),
  resolve: {},
  permissions: [
    { permission: "tenant:space_create", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      name,
      id,
      slug,
      mode,
      grants,
      defaultJoinBuiltinRoleId,
      defaultJoinCustomRoleId,
    },
  }) => {
    if (defaultJoinBuiltinRoleId && defaultJoinCustomRoleId) {
      throw userFacingError(
        "A space can have only one default join role at a time"
      );
    }
    if (defaultJoinBuiltinRoleId) {
      await assertRoleDoesNotGrant(
        tx,
        { builtinRoleId: defaultJoinBuiltinRoleId as BuiltinRoleId },
        "space:manage"
      );
    }
    if (defaultJoinCustomRoleId) {
      await assertRoleDoesNotGrant(
        tx,
        { customRoleId: defaultJoinCustomRoleId },
        "space:manage"
      );
    }

    const existing = await tx.run(zql.spaces);
    assertNameAvailable(existing, name);

    const resolvedSlug = slug ?? slugifyName(name);
    const slugError = slugValidationError(resolvedSlug);
    if (slugError) {
      throw userFacingError(
        slug === undefined
          ? "This name would produce an invalid space URL. Set a custom space URL instead."
          : slugError
      );
    }
    if (slug !== undefined) assertSlugAvailable(existing, slug);

    const now = Date.now();
    try {
      await tx.mutate.spaces.insert({
        id,
        name,
        slug: resolvedSlug,
        mode,
        createdAt: now,
        updatedAt: now,
        defaultJoinBuiltinRoleId:
          (defaultJoinBuiltinRoleId as BuiltinRoleId | null) ?? undefined,
        defaultJoinCustomRoleId: defaultJoinCustomRoleId ?? undefined,
        tenantId: ctx.tenantId,
      });
    } catch (err) {
      if (isUniqueConstraintViolation(err, "spaces_tenant_name_unique")) {
        throw userFacingError("A space with this name already exists");
      }
      if (isUniqueConstraintViolation(err, "spaces_tenant_slug_unique")) {
        throw userFacingError(
          slug
            ? "This space URL is already taken"
            : "A space with a similar name already exists"
        );
      }
      throw err;
    }

    if (grants) {
      validateGrantsInput(grants, "space");
      await insertGrantsDeduped(tx, grants, id, ctx.tenantId);
    }
  },
});
